import { useEffect, useRef, useState } from "react";
import { MembersApiRoutes } from "../../shared/api-routes/member/members-api.routes";
import http from "../../shared/utils/http";
import ClickIcon from "../essentials/icon-buttons/click-icon/ClickIcon";
import AcedInput from "../essentials/input/text/AcedInput";
import UserCard, { UserPreviewInfo } from "../essentials/user-card/UserCard";
import "./SearchIcon.scss";

const SearchIcon: React.FC = () => {
	const [isSearchOpen, setSearchOpen] = useState<boolean>(false);
	const searchContainerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				searchContainerRef.current &&
				!searchContainerRef.current.contains(event.target as Node)
			) {
				setSearchOpen(false);
			}
		};

		if (isSearchOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isSearchOpen]);

	return (
		<div className="search-icon-container" ref={searchContainerRef}>
			<div className={`search-wrapper ${isSearchOpen ? "open" : ""}`}>
				{isSearchOpen && (
					<div className="search-content">
						<Search action="follow" onClose={() => setSearchOpen(false)} />
					</div>
				)}
				<ClickIcon
					color="black"
					icon="search"
					tooltip="Search Members"
					onClick={() => setSearchOpen(!isSearchOpen)}
				/>
			</div>
		</div>
	);
};

export default SearchIcon;

export interface SearchProps {
	action?: "follow" | "message";
	onClose?: () => void;
}
export const Search: React.FC<SearchProps> = ({ action, onClose }) => {
	const [search, setSearch] = useState<string>("");
	const [searchResult, setSearchResult] = useState<UserPreviewInfo[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	useEffect(() => {
		const handler = setTimeout(() => {
			if (search.trim() !== "") {
				setLoading(true);
				setError(null);

				http
					.get<UserPreviewInfo[]>(
						MembersApiRoutes.GET_ALL_USERS + "?q=" + search
					)
					.then((res) => {
						setSearchResult(res);
					})
					.catch(() => setError("Failed to fetch"))
					.finally(() => setLoading(false));
			} else {
				setSearchResult([]);
			}
		}, 300);

		return () => {
			clearTimeout(handler);
		};
	}, [search]);

	return (
		<div className="search-bar">
			<AcedInput
				ref={inputRef}
				value={search}
				onChange={(value) => setSearch(value)}
				placeholder="Search members..."
			/>
			{searchResult.length > 0 && (
				<div className="search-results">
					{searchResult.map((member) => {
						return (
							<div key={member.id}>
								<UserCard
									userInfo={member}
									action={action}
									onActionClick={onClose}
									darkMode={true}
								/>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};
